/* eslint-disable max-statements */
import moment, { Moment } from 'moment';

import {
  CompanyIncrementalDay,
  WeekIdentifier,
  Week
} from './Incrementals.types';

function getMonday(date: string) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  const newDate = new Date(d.setDate(diff));
  return newDate; // moment.utc(newDate);
}

function getSunday(date: string) {
  const d = new Date(date);
  const day = d.getDay(); // will give me day of week
  const diff = d.getDate() - day + (day !== 0 ? +7 : 0); // adjust when day is not sunday
  const newDate = new Date(d.setDate(diff));

  return newDate; // moment.utc(newDate);
}

// Increase date by x amount of days
const increaseDays = (date: Date, amount: number) =>
  new Date(date.setDate(date.getDate() + amount));

export const formatMonthDayMoment = (date: Moment): string => {
  return date.format('MMM DD');
};

export const formatMonthDay = (date: string): string => {
  return formatMonthDayMoment(moment.utc(date));
};

// id1 and id2 should not be null
export const isWeekIdEqual = (id1: WeekIdentifier, id2: WeekIdentifier) => {
  const startOfWeekSame = id1.startOfWeek.isSame(id2.startOfWeek);
  const endOfWeekSame = id1.endOfWeek.isSame(id2.endOfWeek);

  return startOfWeekSame && endOfWeekSame;
};

// Only call this if weekId exists within weeks
export const getWeek = (weekId: WeekIdentifier, weeks: Week[]): Week => {
  const matches = weeks.filter((w) => isWeekIdEqual(weekId, w.weekId));
  return matches[0];
};

// Return an array of days from startDate to endDate.
// Formatted such as ['Jan 02', 'Jan 03', 'Jan 04']
export const dateRangeAsDays = (
  startDate: Moment,
  endDate: Moment
): string[] => {
  const intervals: string[] = [];
  const current = moment(startDate); // make a copy so it is a different memory reference

  while (current <= endDate) {
    intervals.push(formatMonthDayMoment(current));
    current.add(1, 'days');
  }

  return intervals;
};

export const getWeeklyPaginationArray = (
  startDate: string,
  endDate: string
) => {
  const startMonday = getMonday(startDate); // get first Monday of range
  const endSunday = getSunday(endDate); // get next Sunday of range

  const weeks = [];
  let current = startMonday;

  while (current < endSunday) {
    // Get start of the week
    const beginOfWeek = current;
    // Get end of the week
    let endOfWeek = getSunday(current.toString()); // increaseDays(current, 7);
    // If there are less then 7 days left before the end, use the end.
    endOfWeek = endOfWeek > endSunday ? endSunday : endOfWeek;

    // Add week to our collection
    const bowString = formatMonthDay(beginOfWeek.toString());
    const eowString = formatMonthDay(endOfWeek.toString());
    const weekString = bowString.concat(' - ', eowString);
    weeks.push([weekString]);

    current = increaseDays(endOfWeek, 1); // increaseDays(current, 1);
  }

  return weeks;
};

export const getValueForDay = (
  incrementalDate: CompanyIncrementalDay
): string => {
  if (incrementalDate.userValue && incrementalDate.userValue.day) {
    return incrementalDate.userValue.day;
  }

  if (incrementalDate.requested) {
    return incrementalDate.requested.day.toString();
  }

  if (incrementalDate.approved) {
    return incrementalDate.approved.day.toString();
  }

  // There is no requested/approved for this date in database
  // And user has not entered anything in the ui
  // So return 0 for case of "no data"
  return '0';
};

export const getValueForNight = (
  incrementalDate: CompanyIncrementalDay
): string => {
  if (incrementalDate.userValue && incrementalDate.userValue.night) {
    return incrementalDate.userValue.night;
  }

  if (incrementalDate.requested) {
    return incrementalDate.requested.night.toString();
  }

  if (incrementalDate.approved) {
    return incrementalDate.approved.night.toString();
  }

  // There is no requested/approved for this date in database
  // And user has not entered anything in the ui
  // So return 0 for case of "no data"
  return '0';
};

export const getDaySum = (incremental: CompanyIncrementalDay[]) => {
  const daySum = incremental.reduce((sum, current) => {
    const value = Number(getValueForDay(current));

    return sum + value;
  }, 0);
  return daySum;
};

export const getNightSum = (incremental: CompanyIncrementalDay[]) => {
  const nightSum = incremental.reduce((sum, current) => {
    const value = Number(getValueForNight(current));

    return sum + value;
  }, 0);
  return nightSum;
};
